<template>
  <v-form v-model="formValid" ref="form" :disabled="disabled">
    <div class="pl-4">
      <v-row>
        <v-col cols="12"><span class="font-weight-medium">Local:</span></v-col>
      </v-row>
      <v-row no-gutters class="pl-4">
        <v-col cols="12" class="d-flex">
          <v-checkbox
            class="mr-4"
            v-for="option in hospOptions.location"
            :key="option.key"
            :value="option.key"
            v-model="modelValue.locations"
            :label="option.label"
            hide-details
            :rules="[$validation.required()]"
          >
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12"><span class="font-weight-medium">Data:</span></v-col>
      </v-row>
      <v-row>
        <v-col cols="6" class="py-0">
          <v-menu
            v-model="startPickDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                v-bind="attrs"
                v-on="on"
                placeholder="Data de início"
                append-icon="mdi-calendar"
                v-mask="['##/##/####']"
                v-model="modelValue.start_date_formatted"
                @blur="parseStartDate()"
                :rules="[$validation.required()]"
              />
            </template>
            <v-date-picker
              v-model="modelValue.start_date"
              no-title
              @change="modelValue.end_date = null"
              @input="formatStartDate()"
              locale="pt-br"
              :max="$moment().subtract(1, 'days').toISOString()"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="6" class="py-0">
          <v-menu
            v-model="endPickDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                append-icon="mdi-calendar"
                placeholder="Data de fim"
                v-mask="['##/##/####']"
                v-model="modelValue.end_date_formatted"
                @blur="parseEndDate()"
                v-bind="attrs"
                v-on="on"
                :rules="[$validation.required()]"
              />
            </template>
            <v-date-picker
              v-model="modelValue.end_date"
              no-title
              locale="pt-br"
              :min="modelValue.start_date"
              :max="$moment().toISOString()"
              @input="formatEndDate()"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3" offset="9">
          <v-btn color="primary" class="text-none" text @click="addHospitalization()" :disabled="!formValid">
            <v-icon>mdi-plus</v-icon>
            Adicionar outra internação
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="modelValue.previousHospitalization.length > 0">
        <v-col cols="12">
          <div>Internações anteriores</div>
          <ul>
            <li v-for="(hospitalization, index) in modelValue.previousHospitalization" :key="index">
              {{ formatHospitalization(hospitalization, index) }}
            </li>
          </ul>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script>
import { ModelValueProp, PropsDefinition } from '@/_typedefs'
import { HOSPITALIZATION_OPTIONS } from '@/components/AttendanceForm/atopicdermatitis/constants/hospitalization'
import { mask } from 'vue-the-mask'
import { cloneDeep, isEmpty } from 'lodash'

/** @typedef {Record<string, HospitalizationValue>} ModelValue */

/**
 * @typedef {Object} HospitalizationNodeProps
 */

/** @typedef {HospitalizationNodeProps & ModelValueProp<ModelValue>} Props */

export default {
  name: 'HospitalizationNodeComponent',
  directives: {
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  /** @type {PropsDefinition<Props>} */
  props: {
    value: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const baseValue = {
      locations: [],
      start_date: null,
      start_date_formatted: null,
      end_date: null,
      end_date_formatted: null,
      previousHospitalization: [],
    }

    const modelValue = isEmpty(this.value) ? baseValue : cloneDeep(this.value)

    return {
      formValid: false,
      startPickDate: false,
      endPickDate: false,
      modelValue,
      rules: {
        minlength: [(v) => (v && v.length >= 1) || 'O campo não pode estar vazio'],
      },
      hospOptions: HOSPITALIZATION_OPTIONS,
    }
  },
  methods: {
    toggle() {
      this.opened = !this.opened
    },
    resetForm() {
      this.$refs.form.resetValidation()
      this.modelValue.locations = []
      this.modelValue.start_date = null
      this.modelValue.end_date = null
      this.modelValue.start_date_formatted = null
      this.modelValue.end_date_formatted = null
    },
    addHospitalization() {
      this.modelValue.previousHospitalization.push({
        locations: this.modelValue.locations,
        start_date: this.modelValue.start_date,
        end_date: this.modelValue.end_date,
        start_date_formatted: this.modelValue.start_date_formatted,
        end_date_formatted: this.modelValue.end_date_formatted,
      })
      this.resetForm()
    },
    formatHospitalization(hospitalization, index) {
      const locationMap = {
        enfermaria: 'Enfermaria',
        semi: 'Semi-intensiva',
        uti: 'UTI',
      }

      const location = hospitalization.locations.map((l) => locationMap[l]).join(', ')

      const endDate = this.$moment(hospitalization.end_date)
      const startDate = this.$moment(hospitalization.start_date)
      const monthDiff = endDate.diff(startDate, 'months')

      return `Internação ${index + 1}: ${location}. ${monthDiff} meses, de ${hospitalization.start_date_formatted} a ${
        hospitalization.end_date_formatted
      } `
    },
    parseStartDate() {
      this.modelValue.start_date = this.$util.toIsoDate(this.modelValue.start_date_formatted)
    },
    formatStartDate() {
      this.startPickDate = false
      this.modelValue.start_date_formatted = this.$moment(this.modelValue.start_date).format('DD/MM/YYYY')
    },
    parseEndDate() {
      this.modelValue.end_date = this.$util.toIsoDate(this.modelValue.end_date_formatted)
    },
    formatEndDate() {
      this.endPickDate = false
      this.modelValue.end_date_formatted = this.$moment(this.modelValue.end_date).format('DD/MM/YYYY')
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>

<style scoped></style>
